import React, { useState, useEffect } from 'react';
import {
  Webhook,
  Plus,
  Copy,
  Check,
  Trash2,
  Power,
  Loader2,
  Code,
  RefreshCw,
  AlertCircle,
  Eye,
  Send,
  CheckCircle2,
  XCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  PlayCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../components/ui/collapsible';
import { outboundWebhooksAPI } from '../../services/api';
import { cn } from '../../lib/utils';
import { toast } from '../../hooks/use-toast';
import ConfirmDialog from '../../components/ui/confirm-dialog';
import { useConfirm } from '../../components/ui/use-confirm';

const WebhooksPage = () => {
  const [webhooks, setWebhooks] = useState([]);
  const [events, setEvents] = useState({});
  const [signatureInfo, setSignatureInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isLogsOpen, setIsLogsOpen] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [webhookToDelete, setWebhookToDelete] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const { confirm, dialogProps } = useConfirm();

  const [testing, setTesting] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});

  const [newWebhook, setNewWebhook] = useState({
    name: '',
    description: '',
    url: '',
    events: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [webhooksRes, eventsRes] = await Promise.all([
        outboundWebhooksAPI.list(),
        outboundWebhooksAPI.getEvents()
      ]);
      setWebhooks(webhooksRes.data || []);
      setEvents(eventsRes.data?.events || {});
      setSignatureInfo(eventsRes.data?.signature_verification || {});
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newWebhook.name || !newWebhook.url || newWebhook.events.length === 0) {
      toast({
        variant: 'destructive',
        title: 'Missing details',
        description: 'Please fill in name, URL and select at least one event.'
      });
      return;
    }
    
    try {
      await outboundWebhooksAPI.create(newWebhook);
      await fetchData();
      setIsCreateOpen(false);
      setNewWebhook({ name: '', description: '', url: '', events: [] });
    } catch (error) {
      console.error('Failed to create webhook:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to create webhook',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleToggle = async (webhookId) => {
    try {
      await outboundWebhooksAPI.toggle(webhookId);
      await fetchData();
    } catch (error) {
      console.error('Failed to toggle webhook:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to update webhook',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleDelete = async () => {
    if (!webhookToDelete) return;

    const ok = await confirm({
      title: 'Delete webhook?',
      description: `This will remove the webhook “${webhookToDelete.name}”.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      destructive: true
    });
    if (!ok) return;

    try {
      await outboundWebhooksAPI.delete(webhookToDelete.id);
      await fetchData();
      setDeleteConfirmOpen(false);
      setWebhookToDelete(null);
      toast({ title: 'Webhook deleted' });
    } catch (error) {
      console.error('Failed to delete webhook:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to delete webhook',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleTest = async (webhook) => {
    setTesting(true);
    setTestResult(null);
    try {
      const response = await outboundWebhooksAPI.test(webhook.id);
      setTestResult(response.data);
    } catch (error) {
      setTestResult({ success: false, error_message: error.message });
    } finally {
      setTesting(false);
    }
  };

  const handleViewLogs = async (webhook) => {
    try {
      const response = await outboundWebhooksAPI.getLogs(webhook.id);
      setWebhookLogs(response.data || []);
      setSelectedWebhook(webhook);
      setIsLogsOpen(true);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to fetch logs',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleViewDetails = (webhook) => {
    setSelectedWebhook(webhook);
    setIsDetailsOpen(true);
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const toggleEvent = (event) => {
    if (newWebhook.events.includes(event)) {
      setNewWebhook({ ...newWebhook, events: newWebhook.events.filter(e => e !== event) });
    } else {
      setNewWebhook({ ...newWebhook, events: [...newWebhook.events, event] });
    }
  };

  const toggleRowExpand = (id) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        <span className="ml-2 text-slate-500">Loading webhooks...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Outbound Webhooks</h1>
          <p className="text-slate-500 dark:text-slate-400">
            Send real-time event notifications to external systems
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button className="bg-slate-800 hover:bg-slate-900" onClick={() => setIsCreateOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Create Webhook
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-purple-50 border-purple-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Webhook className="w-5 h-5 text-purple-600 mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-purple-800">Event-Driven Webhooks</p>
              <p className="text-sm text-purple-700">
                Configure webhooks to receive real-time notifications when events occur in your CRM.
                Each webhook includes HMAC-SHA256 signature for security verification.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Available Events */}
      <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Available Events</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
            {Object.entries(events).map(([key, event]) => (
              <div key={key} className="p-2 bg-slate-50 dark:bg-slate-700 rounded border text-xs">
                <p className="font-medium text-slate-800 dark:text-slate-100">{event.name}</p>
                <p className="text-slate-500 dark:text-slate-400 truncate">{key}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Webhooks Table */}
      <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 dark:bg-slate-900">
                <TableHead className="w-8"></TableHead>
                <TableHead className="font-semibold">Name</TableHead>
                <TableHead className="font-semibold">URL</TableHead>
                <TableHead className="font-semibold">Events</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold">Deliveries</TableHead>
                <TableHead className="w-[180px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {webhooks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                    <Webhook className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p className="font-medium">No webhooks configured</p>
                    <p className="text-sm">Create a webhook to start receiving event notifications</p>
                  </TableCell>
                </TableRow>
              ) : webhooks.map((webhook) => (
                <React.Fragment key={webhook.id}>
                  <TableRow className="hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer" onClick={() => toggleRowExpand(webhook.id)}>
                    <TableCell className="w-8">
                      {expandedRows[webhook.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-slate-800 dark:text-slate-100">{webhook.name}</p>
                        {webhook.description && (
                          <p className="text-xs text-slate-500">{webhook.description}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="text-xs bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded font-mono truncate block max-w-[200px]">
                        {webhook.url}
                      </code>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(webhook.events || []).slice(0, 2).map(event => (
                          <Badge key={event} variant="outline" className="text-xs">
                            {event.replace(/_/g, ' ')}
                          </Badge>
                        ))}
                        {(webhook.events || []).length > 2 && (
                          <Badge variant="outline" className="text-xs">
                            +{webhook.events.length - 2}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={cn(
                        webhook.is_active 
                          ? 'bg-emerald-100 text-emerald-700' 
                          : 'bg-slate-100 text-slate-600'
                      )}>
                        {webhook.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <span className="text-emerald-600">{webhook.successful_sent || 0}</span>
                        {' / '}
                        <span className="text-red-600">{webhook.failed_sent || 0}</span>
                        <span className="text-slate-400"> / {webhook.total_sent || 0}</span>
                      </div>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2"
                          onClick={() => handleViewDetails(webhook)}
                        >
                          <Code className="w-4 h-4 mr-1" />
                          Details
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => handleViewLogs(webhook)}
                          title="View Logs"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className={cn(
                            'h-8 w-8 p-0',
                            webhook.is_active ? 'text-emerald-600' : 'text-slate-400'
                          )}
                          onClick={() => handleToggle(webhook.id)}
                          title={webhook.is_active ? 'Disable' : 'Enable'}
                        >
                          <Power className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                          onClick={() => { setWebhookToDelete(webhook); setDeleteConfirmOpen(true); }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  {expandedRows[webhook.id] && (
                    <TableRow>
                      <TableCell colSpan={7} className="bg-slate-50 dark:bg-slate-900 p-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-xs text-slate-500">Secret Key</Label>
                            <div className="flex items-center gap-2 mt-1">
                              <code className="text-xs bg-white dark:bg-slate-800 px-2 py-1 rounded font-mono">
                                {webhook.secret_key}
                              </code>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={() => copyToClipboard(webhook.secret_key, `secret-${webhook.id}`)}
                              >
                                {copiedField === `secret-${webhook.id}` ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
                              </Button>
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">Last Triggered</Label>
                            <p className="text-sm mt-1">
                              {webhook.last_triggered_at 
                                ? new Date(webhook.last_triggered_at).toLocaleString('en-IN')
                                : 'Never'}
                            </p>
                          </div>
                          <div className="col-span-2">
                            <Label className="text-xs text-slate-500">Subscribed Events</Label>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {(webhook.events || []).map(event => (
                                <Badge key={event} variant="outline" className="text-xs">
                                  {event}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          {webhook.last_error && (
                            <div className="col-span-2">
                              <Label className="text-xs text-red-500">Last Error</Label>
                              <p className="text-sm text-red-600 mt-1">{webhook.last_error}</p>
                            </div>
                          )}
                          <div className="col-span-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleTest(webhook)}
                              disabled={testing}
                            >
                              {testing ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <PlayCircle className="w-4 h-4 mr-2" />
                              )}
                              Send Test Webhook
                            </Button>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Modal */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Webhook className="w-5 h-5" />
              Create Outbound Webhook
            </DialogTitle>
            <DialogDescription>
              Configure a webhook to receive event notifications
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={newWebhook.name}
                onChange={(e) => setNewWebhook({ ...newWebhook, name: e.target.value })}
                placeholder="e.g., Slack Notifications"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={newWebhook.description}
                onChange={(e) => setNewWebhook({ ...newWebhook, description: e.target.value })}
                placeholder="Optional description"
              />
            </div>
            <div className="space-y-2">
              <Label>Webhook URL *</Label>
              <Input
                value={newWebhook.url}
                onChange={(e) => setNewWebhook({ ...newWebhook, url: e.target.value })}
                placeholder="https://your-server.com/webhook"
              />
            </div>
            <div className="space-y-2">
              <Label>Events to Subscribe *</Label>
              <div className="grid grid-cols-2 gap-2 max-h-[300px] overflow-y-auto p-2 bg-slate-50 rounded">
                {Object.entries(events).map(([key, event]) => (
                  <div key={key} className="flex items-start space-x-2 p-2 hover:bg-white rounded">
                    <Checkbox
                      id={key}
                      checked={newWebhook.events.includes(key)}
                      onCheckedChange={() => toggleEvent(key)}
                    />
                    <div className="grid gap-0.5 leading-none">
                      <label htmlFor={key} className="text-sm font-medium cursor-pointer">
                        {event.name}
                      </label>
                      <p className="text-xs text-slate-500">{event.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleCreate}
              disabled={!newWebhook.name || !newWebhook.url || newWebhook.events.length === 0}
              className="bg-slate-800 hover:bg-slate-900"
            >
              Create Webhook
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Details Modal */}
      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Code className="w-5 h-5" />
              Webhook Details - {selectedWebhook?.name}
            </DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="signature" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="signature">Signature Verification</TabsTrigger>
              <TabsTrigger value="payloads">Sample Payloads</TabsTrigger>
            </TabsList>
            
            <TabsContent value="signature" className="space-y-4">
              <div className="space-y-2">
                <Label>Your Webhook Secret</Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-3 bg-slate-100 dark:bg-slate-800 rounded text-sm font-mono break-all">
                    {selectedWebhook?.secret_key}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(selectedWebhook?.secret_key || '', 'secret-detail')}
                  >
                    {copiedField === 'secret-detail' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Headers Sent with Each Webhook</Label>
                <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded text-sm space-y-1">
                  <div className="flex justify-between">
                    <code>X-Webhook-Signature:</code>
                    <span className="text-slate-500">HMAC-SHA256 signature</span>
                  </div>
                  <div className="flex justify-between">
                    <code>X-Webhook-Timestamp:</code>
                    <span className="text-slate-500">ISO 8601 timestamp</span>
                  </div>
                  <div className="flex justify-between">
                    <code>X-Webhook-Version:</code>
                    <span className="text-slate-500">v1</span>
                  </div>
                  <div className="flex justify-between">
                    <code>Content-Type:</code>
                    <span className="text-slate-500">application/json</span>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Signature Verification (Python)</Label>
                <pre className="p-4 bg-slate-900 text-slate-100 rounded text-xs overflow-x-auto">
{`import hmac
import hashlib

def verify_webhook(request_body: str, timestamp: str, signature: str, secret: str) -> bool:
    signature_base = f"{timestamp}.{request_body}"
    expected = hmac.new(
        secret.encode('utf-8'),
        signature_base.encode('utf-8'),
        hashlib.sha256
    ).hexdigest()
    return hmac.compare_digest(expected, signature)`}
                </pre>
              </div>
              
              <div className="space-y-2">
                <Label>Signature Verification (Node.js)</Label>
                <pre className="p-4 bg-slate-900 text-slate-100 rounded text-xs overflow-x-auto">
{`const crypto = require('crypto');

function verifyWebhook(requestBody, timestamp, signature, secret) {
    const signatureBase = \`\${timestamp}.\${requestBody}\`;
    const expected = crypto
        .createHmac('sha256', secret)
        .update(signatureBase)
        .digest('hex');
    return crypto.timingSafeEqual(
        Buffer.from(expected),
        Buffer.from(signature)
    );
}`}
                </pre>
              </div>
            </TabsContent>
            
            <TabsContent value="payloads" className="space-y-4">
              <p className="text-sm text-slate-500">
                Sample payloads for each subscribed event:
              </p>
              {(selectedWebhook?.events || []).map(eventKey => {
                const event = events[eventKey];
                if (!event) return null;
                return (
                  <Collapsible key={eventKey}>
                    <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-slate-50 hover:bg-slate-100 rounded">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{eventKey}</Badge>
                        <span className="font-medium">{event.name}</span>
                      </div>
                      <ChevronDown className="w-4 h-4" />
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <pre className="p-4 bg-slate-900 text-slate-100 rounded-b text-xs overflow-x-auto">
                        {JSON.stringify(event.sample_payload, null, 2)}
                      </pre>
                    </CollapsibleContent>
                  </Collapsible>
                );
              })}
            </TabsContent>
          </Tabs>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDetailsOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Logs Modal */}
      <Dialog open={isLogsOpen} onOpenChange={setIsLogsOpen}>
        <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Delivery Logs - {selectedWebhook?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="max-h-[500px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Event</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Response</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {webhookLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                      No delivery logs yet
                    </TableCell>
                  </TableRow>
                ) : webhookLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      <Badge variant="outline">{log.event_type}</Badge>
                    </TableCell>
                    <TableCell>
                      {log.is_success ? (
                        <Badge className="bg-emerald-100 text-emerald-700">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Success
                        </Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-700">
                          <XCircle className="w-3 h-3 mr-1" />
                          Failed
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {log.response_status ? (
                        <span className={cn(
                          log.response_status >= 200 && log.response_status < 300 ? 'text-emerald-600' : 'text-red-600'
                        )}>
                          HTTP {log.response_status}
                        </span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                      {log.error_message && (
                        <p className="text-xs text-red-500 truncate max-w-[200px]">{log.error_message}</p>
                      )}
                    </TableCell>
                    <TableCell>
                      {log.response_time_ms ? `${log.response_time_ms}ms` : '-'}
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {new Date(log.created_at).toLocaleString('en-IN')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsLogsOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Result Alert */}
      {testResult && (
        <AlertDialog open={!!testResult} onOpenChange={() => setTestResult(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                {testResult.success ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-600" />
                )}
                Test Webhook {testResult.success ? 'Succeeded' : 'Failed'}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {testResult.success ? (
                  <div className="space-y-2">
                    <p>Webhook delivered successfully!</p>
                    <p className="text-sm">Response: HTTP {testResult.response_status} ({testResult.response_time_ms}ms)</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p>Failed to deliver webhook</p>
                    {testResult.error_message && (
                      <p className="text-sm text-red-600">{testResult.error_message}</p>
                    )}
                  </div>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction onClick={() => setTestResult(null)}>OK</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {/* Delete Confirmation */}
      {/* Delete confirmation now uses shared ConfirmDialog */}
      <ConfirmDialog
        open={dialogProps.open}
        onOpenChange={dialogProps.onOpenChange}
        title={dialogProps.title}
        description={dialogProps.description}
        confirmText={dialogProps.confirmText}
        cancelText={dialogProps.cancelText}
        destructive={dialogProps.destructive}
        onConfirm={dialogProps.onConfirm}
        onCancel={dialogProps.onCancel}
      />
    </div>
  );
};

export default WebhooksPage;
